import type { TextDetection } from '../../detect/pii/types';
import type { PdfPageInfo, PdfTextItem } from '../../pipeline/pdf';

/**
 * Convert a char-range slice of a pdfjs text item to a viewport-space
 * rectangle. Linear interpolation across the item's text — works well
 * for monospace and well enough for proportional fonts.
 */
export const itemRectForCharRange = (
  item: PdfTextItem,
  charStart: number,
  charEnd: number,
  pageHeight: number,
  scale: number,
): { x: number; y: number; w: number; h: number } => {
  const itemLen = Math.max(1, item.text.length);
  const localStart = Math.max(0, charStart - item.start);
  const localEnd = Math.min(item.text.length, charEnd - item.start);
  const startFrac = localStart / itemLen;
  const endFrac = localEnd / itemLen;

  const [a, , , d, e, f] = item.transform;
  const widthPdf = item.width;
  const heightPdf = item.height || Math.abs(d);
  const xPdf = e + startFrac * widthPdf * Math.sign(a || 1);
  const wPdf = (endFrac - startFrac) * widthPdf;
  // 2px of padding on each side. The character-width interpolation in
  // proportional fonts is approximate, and fillRect aliases on
  // fractional pixel boundaries — without this, the last glyph of a
  // detection span peeks out the right edge of the redaction box.
  const PAD = 2;
  const xCanvas = xPdf * scale - PAD;
  const yCanvas = (pageHeight - f - heightPdf) * scale - PAD;
  return {
    x: xCanvas,
    y: yCanvas,
    w: wPdf * scale + PAD * 2,
    h: heightPdf * scale + PAD * 2,
  };
};

/**
 * Paint black rectangles over every selected text detection on a page.
 * Caller is responsible for ensuring the canvas already has the
 * rasterized page underneath.
 */
export const drawTextDetections = (
  ctx: CanvasRenderingContext2D,
  page: PdfPageInfo,
  selected: readonly TextDetection[],
  scale: number,
): void => {
  ctx.save();
  ctx.fillStyle = '#000';
  for (const d of selected) {
    for (const item of page.items) {
      if (d.end <= item.start || d.start >= item.end) continue;
      const rect = itemRectForCharRange(
        item,
        Math.max(d.start, item.start),
        Math.min(d.end, item.end),
        page.heightPdf,
        scale,
      );
      if (rect.w <= 0 || rect.h <= 0) continue;
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    }
  }
  ctx.restore();
};

export const canvasToPng = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });

/**
 * Paint user-drawn manual rectangles onto a canvas. Rects are
 * normalized [0..1] of the page; we multiply by the canvas dimensions
 * to get pixel-space coordinates. Same function is used by the live
 * preview and by the PDF/image redact output paths so what the user
 * sees is what they download.
 */
export const drawManualRects = (
  ctx: CanvasRenderingContext2D,
  rects: readonly { x: number; y: number; w: number; h: number }[],
  canvasWidth: number,
  canvasHeight: number,
): void => {
  if (rects.length === 0) return;
  ctx.save();
  ctx.fillStyle = '#000';
  for (const r of rects) {
    if (r.w <= 0 || r.h <= 0) continue;
    ctx.fillRect(
      Math.round(r.x * canvasWidth),
      Math.round(r.y * canvasHeight),
      Math.round(r.w * canvasWidth),
      Math.round(r.h * canvasHeight),
    );
  }
  ctx.restore();
};
